import { NavLink, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { MdDashboard, MdAdminPanelSettings, MdSchool } from "react-icons/md";
import { IoContract, IoDocumentText } from "react-icons/io5";
import { BiPurchaseTag } from "react-icons/bi";
import { FaBrain } from 'react-icons/fa';
import { HiOutlineChevronRight, HiOutlineChevronDown } from "react-icons/hi";
import { hasMenuAccess, hasGeneradorAccess, nombrePlan, SECCION_MENORES_VIGENTES, SECCION_TRABAJA_ESTADO } from './planPermissions';
import useAccesosPortal from '../hooks/useAccesosPortal';
import {
    FaBars,
    FaTimes,
    FaCalendarAlt,
    FaChartBar,
    FaSignOutAlt,
    FaBookOpen,
    FaUserGraduate,
    FaPlay,
    FaVideo,
    FaExternalLinkAlt,
    FaGlobeAmericas,
    FaGavel,
    FaCalculator,
    FaFileDownload,
    FaGraduationCap,
    FaChalkboardTeacher,
    FaBinoculars,
    FaMoneyCheckAlt,
    FaClipboardList,
    FaBriefcase
} from "react-icons/fa";

const Sidebar = ({ user, isCollapsed, onToggle, onLogout }) => {
    const [expandedMenus, setExpandedMenus] = useState({});
    const [floatingSubmenu, setFloatingSubmenu] = useState({
        isOpen: false,
        menuIndex: null,
        position: { top: 0, left: 0 }
    });
    const [isMobile, setIsMobile] = useState(false);
    const [isTablet, setIsTablet] = useState(false);
    const [overlayVisible, setOverlayVisible] = useState(false);
    // Planes con acceso al "Generador de Oferta" y catálogo de grupos del plan
    // Curso (cada grupo con su perfil), ambos configurados por el admin.
    const { generadorPlanes, grupos: gruposCurso } = useAccesosPortal();
    const navigate = useNavigate();

    // Detectar tamaño de dispositivo
    useEffect(() => {
        const checkDevice = () => {
            const width = window.innerWidth;
            const wasMobile = isMobile;
            const wasTablet = isTablet;

            setIsMobile(width <= 768);
            setIsTablet(width > 768 && width <= 1024);

            // Auto-colapsar en tablet
            if (width > 768 && width <= 1024 && !wasTablet && !isCollapsed) {
                onToggle?.(true);
            }

            // Colapsar cuando cambiamos de desktop a móvil
            if (width <= 768 && !wasMobile && !isCollapsed) {
                onToggle?.(true);
            }
        };

        checkDevice();
        window.addEventListener('resize', checkDevice);
        return () => window.removeEventListener('resize', checkDevice);
    }, [isMobile, isTablet, isCollapsed, onToggle]);

    // Controlar overlay y scroll del body
    useEffect(() => {
        if (isMobile) {
            setOverlayVisible(!isCollapsed);
            document.body.style.overflow = isCollapsed ? 'unset' : 'hidden';
        } else {
            setOverlayVisible(false);
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isCollapsed, isMobile]);

    const userPlan = user?.plan || 'MENOR';
    const cursoTipo = user?.curso_tipo;
    // Lo que ve el usuario en su insignia. Para un alumno del plan Curso es el
    // plan al que equivale su grupo, no la palabra «Curso».
    const planMostrado = nombrePlan(userPlan, cursoTipo, gruposCurso);

    // ADMI siempre tiene acceso; el resto según lo que el admin haya habilitado
    // (incluye grupos específicos del plan Curso a través de curso_tipo)
    const tieneAccesoGenerador = hasGeneradorAccess(userPlan, cursoTipo, generadorPlanes, gruposCurso);
    const tieneAccesoSeace = hasMenuAccess(userPlan, 'SEACE', cursoTipo, gruposCurso);
    const tieneAccesoMenores = hasMenuAccess(userPlan, 'Contratos Menores', cursoTipo, gruposCurso);
    // «Convocatorias Vigentes» está en SECCIONES_LIBRES: la ve cualquier plan.
    const tieneAccesoMenoresVigentes = hasMenuAccess(userPlan, SECCION_MENORES_VIGENTES, cursoTipo, gruposCurso);

    const toggleSidebar = () => {
        if (onToggle) {
            if (isMobile) {
                setTimeout(() => onToggle(!isCollapsed), 50);
            } else {
                onToggle(!isCollapsed);
            }
        }
    };

    const allMenuItems = [
        {
            label: "Dashboard",
            path: "/",
            icon: MdDashboard,
            description: "Panel principal",
            color: "#667eea"
        },
        {
            label: "Estadísticas",
            path: "/estadisticas",
            icon: FaChartBar,
            description: "Análisis de adjudicaciones",
            color: "#6d28d9"
        },
        {
            label: "Contratos Menores",
            path: "/Contratos menores",
            icon: IoContract,
            description: "Gestión de contratos menores",
            color: "#28a745",
            children: [
                ...(tieneAccesoMenores ? [{
                    label: "Nuevas Convocatorias",
                    path: "/Contratos menores/nuevas",
                    icon: FaCalendarAlt,
                    description: "Convocatorias más recientes"
                }] : []),
                ...(tieneAccesoMenoresVigentes ? [{
                    label: "Convocatorias Vigentes",
                    path: "/Contratos menores/vigentes",
                    icon: FaClipboardList,
                    description: "Abiertas a cotización"
                }] : []),
                ...(tieneAccesoMenores ? [{
                    label: "Vista General",
                    path: "/Contratos menores",
                    icon: FaChartBar,
                    description: "Visualización del reporte general"
                }] : [])
            ]
        },
    {
    label: "Contratos Menores (Old)",
    path: "/Contratos menores (Old)",
    icon: IoContract,
    description: "Gestión de contratos menores - Versión anterior",
    color: "#6c757d", // Color gris para indicar que es versión antigua
    children: [

        {
            label: "Vista General",
            path: "/Contratos menores (Old)",
            icon: FaChartBar,
            description: "Visualización del reporte general"
        }
    ]
},
        {
            path: "/SEACE",
            icon: IoDocumentText,
            label: "SEACE",
            description: "Gestión de contratos mayores",
            color: "#fd7e14",
            children: [
                ...(tieneAccesoSeace ? [
                    {
                        label: "Nuevas Convocatorias",
                        path: "/SEACE/nuevos",
                        icon: FaCalendarAlt,
                        description: "Últimas convocatorias publicadas"
                    },
                    {
                        label: "Vista General",
                        path: "/SEACE",
                        icon: FaChartBar,
                        description: "Convocatorias vigentes en SEACE"
                    }
                ] : []),
                ...(tieneAccesoGenerador ? [{
                    label: "Generador de Oferta",
                    path: "/SEACE/generador-oferta",
                    icon: IoDocumentText,
                    description: "Genera documentos de oferta"
                }] : [])
            ]
        },
        {
            label: "SEACE Prueba",
            path: "/SEACE_PRUEBA",
            icon: FaChartBar,
            description: "Nuevas convocatorias SEACE",
        },
        {
            label: "PAC",
            path: "/PAC",
            icon: BiPurchaseTag,
            description: "Plan Anual de Contrataciones",
            color: "#6f42c1",
            children: [
                {
                    label: "Procesos Convocados",
                    path: "/PAC",
                    icon: FaChartBar,
                    description: "PAC - general"
                },
                {
                    label: "Procesos No Convocados",
                    path: "/PAC/nuevas",
                    icon: FaCalendarAlt,
                    description: "PAC - nuevas convocatorias"
                }
            ]
        },
        {
            label: "Calendario",
            path: "/calendario",
            icon: FaCalendarAlt,
            description: "Fechas importantes de procesos",
            color: "#e91e63"
        },
        {
    label: "Cursos",
    path: "/cursos",
    icon: MdSchool,
    description: "Academia de licitaciones públicas",
    color: "#17a2b8",
    isNew: true,
    children: [
        ...(userPlan === 'ADMI' ? [{
            label: "Gestión de Cursos",
            path: "/cursos/admin",
            icon: FaBookOpen,
            description: "Administrar cursos disponibles"
        }] : []),
        {
            label: "Academia de Licitaciones",
            path: "/cursos/academia",
            icon: FaGraduationCap,
            description: "Plataforma de cursos completos"
        },
        {
            label: "Catálogo Simple",
            path: "/cursos",
            icon: FaUserGraduate,
            description: "Ver cursos disponibles y solicitar información"
        }
    ]
},
    {
        label: "Aprende a Licitar",
        path: "/aprende-licitar",
        icon: FaGraduationCap,
        description: "Plataforma de aprendizaje de licitaciones",
        color: "#10b981"
    },
{
    label: "Tutoriales",
    path: "/tutoriales",
    icon: FaVideo,
    description: "Videos tutoriales del sistema",
    color: "#e91e63",
    children: [
        {
            label: "Introducción a LicitaHub",
            path: "/tutoriales/introduccion-licitahub",
            icon: FaPlay,
            description: "Guía inicial para usar la plataforma"
        },
        {
            label: "Contratos Menores",
            path: "/tutoriales/procesos-menores-8-uit",
            icon: FaPlay,
            description: "Como buscar procesos menores a 8 UIT"
        },
        {
            label: "Convocatorias del SEACE",
            path: "/tutoriales/busqueda-avanzada-seace",
            icon: FaPlay,
            description: "Técnicas de búsqueda avanzada"
        },
        {
            label: "Seguimiento de convocatorias",
            path: "/tutoriales/seguimiento-convocatorias",
            icon: FaPlay,
            description: "El calendario y el tablero de seguimiento"
        }
    ]
},
        {
            label: "Talleres",
            path: "/talleres",
            icon: FaChalkboardTeacher,
            description: "Videos de talleres y capacitaciones",
            color: "#f59e0b",
            isNew: true
        },
        {
            label: "Recursos Útiles",
            path: "/recursos",
            icon: FaExternalLinkAlt,
            description: "Enlaces útiles para contrataciones públicas",
            color: "#8b5cf6",
            isNew: true,
            children: [
                {
                    label: "Sitios Oficiales",
                    path: "/recursos/sitios-oficiales",
                    icon: FaGlobeAmericas,
                    description: "Portales gubernamentales oficiales"
                }
            ]
        },
            {
        label: "Predicción con IA",
        path: "/prediccion-ia",
        icon: FaBrain,
        description: "Predicción de valor de licitaciones públicas",
        color: "#6366f1",
        isNew: true
    },
        {
            label: "Análisis de Competidor",
            path: "/analisis-competidor",
            icon: FaBinoculars,
            description: "Historial de contrataciones de un competidor",
            color: "#0f766e",
            isNew: true
        },
        {
            label: "Seguimiento de Pagos",
            path: "/seguimiento-pagos",
            icon: FaMoneyCheckAlt,
            description: "Estado del expediente SIAF en el MEF",
            color: "#0f766e",
            isNew: true
        },
        {
            label: SECCION_TRABAJA_ESTADO,
            path: "/trabaja-estado",
            icon: FaBriefcase,
            description: "Convocatorias de empleo público (CAS)",
            color: "#0f766e",
            isNew: true
        },
        {
            label: "Catálogo de Proveedores",
            path: "/proveedores",
            externalUrl: "https://proveeperu.com/buscar",
            icon: MdAdminPanelSettings,
            description: "Marketplace de servicios para el sector público",
            color: "#667eea",
            isNew: true
        },
        {
            path: "/admin",
            label: "Administración",
            icon: MdAdminPanelSettings,
            description: "Gestión de suscriptores",
            color: "#dc3545"
        }
    ];

    const menuItems = allMenuItems.filter(item => {
        // El menú SEACE también se muestra si el admin concedió acceso al
        // Generador de Oferta a un plan que normalmente no ve SEACE.
        if (item.label === 'SEACE') {
            return tieneAccesoSeace || tieneAccesoGenerador;
        }
        // Igual que SEACE con el Generador: el menú de menores también se
        // muestra a quien solo tiene «Convocatorias Vigentes» (sección libre).
        if (item.label === 'Contratos Menores') {
            return tieneAccesoMenores || tieneAccesoMenoresVigentes;
        }
        return hasMenuAccess(userPlan, item.label, cursoTipo, gruposCurso);
    });

    const toggleSubmenu = (index) => {
        if ((isCollapsed && !isMobile) || isTablet) return;
        setExpandedMenus(prev => ({
            ...prev,
            [index]: !prev[index]
        }));
    };

    // FIX: Removida la restricción de isTablet para que funcione en tablets
    const handleCollapsedMenuClick = (item, index, event) => {
        if (isMobile) return; // Solo bloquear en móvil

        if (!item.children || item.children.length === 0) {
            if (item.externalUrl) {
                window.open(item.externalUrl, '_blank');
            } else {
                navigate(item.path);
            }
            return;
        }

        const rect = event.currentTarget.getBoundingClientRect();
        const position = {
            top: rect.top + window.scrollY,
            left: rect.right + 15
        };

        if (floatingSubmenu.isOpen && floatingSubmenu.menuIndex === index) {
            closeFloatingSubmenu();
        } else {
            setFloatingSubmenu({ isOpen: true, menuIndex: index, position });
        }
    };

    const closeFloatingSubmenu = () => {
        setFloatingSubmenu({ isOpen: false, menuIndex: null, position: { top: 0, left: 0 } });
    };

    const handleParentMenuClick = (item, index, event) => {
        if (isMobile) {
            toggleSubmenu(index);
        } else if (isCollapsed || isTablet) {
            handleCollapsedMenuClick(item, index, event);
        } else {
            toggleSubmenu(index);
        }
    };

    const handleMobileNavigation = (path, externalUrl) => {
        if (externalUrl) {
            window.open(externalUrl, '_blank');
        } else {
            navigate(path);
        }
        if (isMobile && onToggle) {
            onToggle(true);
        }
    };

    // FIX: Removida la restricción de isTablet en el useEffect
    useEffect(() => {
        if (isMobile) return; // Solo bloquear en móvil

        const handleClickOutside = (event) => {
            if (floatingSubmenu.isOpen) {
                const floatingMenu = document.querySelector('.floating-submenu');
                const sidebar = document.querySelector('.sidebar');

                if (floatingMenu && !floatingMenu.contains(event.target) &&
                    sidebar && !sidebar.contains(event.target)) {
                    closeFloatingSubmenu();
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [floatingSubmenu.isOpen, isMobile]);

    // FIX: Removida la restricción de isTablet para cerrar el menú flotante
    useEffect(() => {
        if (!isCollapsed || isMobile) {
            closeFloatingSubmenu();
        }
    }, [isCollapsed, isMobile]);

    useEffect(() => {
        if ((isCollapsed && !isMobile) || isTablet) {
            setExpandedMenus({});
        }
    }, [isCollapsed, isMobile, isTablet]);

    const handleLogout = () => {
        const confirmLogout = window.confirm('¿Estás seguro de que deseas cerrar sesión?');
        if (confirmLogout && onLogout) {
            onLogout();
        }
    };
    return (
        <>
            {/* Overlay para móvil */}
            {isMobile && overlayVisible && (
                <div
                    className="mobile-overlay"
                    onClick={toggleSidebar}
                />
            )}

            {/* Botón toggle para móvil */}
            {isMobile && (
                <button
                    className="mobile-toggle-btn"
                    onClick={toggleSidebar}
                    title={isCollapsed ? "Abrir menú" : "Cerrar menú"}
                >
                    {isCollapsed ? <FaBars size={20} /> : <FaTimes size={20} />}
                </button>
            )}

            <div className={`sidebar ${isCollapsed ? 'collapsed' : ''} ${isMobile ? 'mobile' : ''} ${isTablet ? 'tablet' : ''}`}>
                {/* Botón toggle para desktop */}
                {!isMobile && !isTablet && (
                    <div className="toggle-container">
                        <button
                            className="toggle-btn"
                            onClick={toggleSidebar}
                            title={isCollapsed ? "Expandir sidebar" : "Contraer sidebar"}
                        >
                            {isCollapsed ? <FaBars size={14} /> : <FaTimes size={14} />}
                        </button>
                    </div>
                )}

                {/* Header del sidebar */}
                <div className="sidebar-header">
                    <div className="sidebar-brand">
                        <div className="logo-container">
                            <img
                                src={`${process.env.PUBLIC_URL}/assets/favicon.svg?v=3`}
                                alt="LicitaHub"
                                className="company-logo"
                            />
                        </div>
                        {((!isCollapsed && !isTablet) || isMobile) && (
                            <div className="brand-text">
                                <h3 className="brand-name">LicitaHub</h3>
                                <span className="brand-subtitle">Versión empresarial</span>
                            </div>
                        )}
                    </div>
                </div>

              {/* Información del usuario */}
{user && (
    <div className="user-section">
        <div className="user-info">
            {/* Solo mostrar avatar si existe picture */}
            {user.picture && (
                <div className="user-avatar-container">
                    <img
                        src={user.picture}
                        alt={user.name}
                        className="user-avatar"
                        onError={(e) => {
                            e.target.style.display = 'none';
                        }}
                    />
                    <div className="user-status"></div>
                </div>
            )}
            {/* Mostrar un icono alternativo si no hay picture */}
            {!user.picture && (
                <div className="user-avatar-container">
                    <div className="user-avatar-placeholder">
                        {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                    </div>
                    <div className="user-status"></div>
                </div>
            )}
            {((!isCollapsed && !isTablet) || isMobile) && (
                <div className="user-details">
                    <div className="user-name" title={user.name}>{user.name}</div>
                    <div className="user-email" title={user.email}>{user.email}</div>
                    <span className="user-plan-badge" title={'Tu plan: ' + planMostrado}>
                        <BiPurchaseTag size={12} aria-hidden="true" />
                        <span>{planMostrado}</span>
                    </span>
                </div>
            )}
        </div>
    </div>
)}
                {/* Navegación */}
                <nav className="sidebar-nav">
                    <div className="nav-section">
                        <div className="nav-section-title">
                            {((!isCollapsed && !isTablet) || isMobile) && <span>NAVEGACIÓN</span>}
                        </div>
                        <ul className="nav-list">
                            {menuItems.map((item, index) => {
                                const IconComponent = item.icon;
                                const hasChildren = item.children && item.children.length > 0;
                                const isExpanded = expandedMenus[index];

                                if (hasChildren) {
                                    return (
                                        <li key={index} className="nav-item">
                                            {((isCollapsed && !isMobile) || isTablet) ? (
                                                <div
                                                    className={`nav-link parent-nav-collapsed ${floatingSubmenu.isOpen && floatingSubmenu.menuIndex === index ? 'active' : ''}`}
                                                    onClick={(e) => handleParentMenuClick(item, index, e)}
                                                    title={item.description}
                                                    style={{ cursor: 'pointer', pointerEvents: 'auto' }}
                                                >
                                                    <div className="nav-link-content">
                                                        <div className="nav-icon">
                                                            <IconComponent size={17} />
                                                        </div>
                                                        <div className="submenu-indicator">
                                                            <HiOutlineChevronRight size={12} />
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : (
                                                <>
                                                    <div
                                                        className={`nav-link parent-nav ${isExpanded ? 'expanded' : ''}`}
                                                        onClick={(e) => handleParentMenuClick(item, index, e)}
                                                        title={item.description}
                                                    >
                                                        <div className="nav-link-content">
                                                            <div className="nav-icon">
                                                                <IconComponent size={17} />
                                                            </div>
                                                            <span className="nav-label">{item.label}</span>
                                                            {item.isNew && <span className="new-badge">NEW</span>}
                                                            <div className="nav-arrow">
                                                                {isExpanded ?
                                                                    <HiOutlineChevronDown size={16} /> :
                                                                    <HiOutlineChevronRight size={16} />
                                                                }
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <ul className={`submenu ${isExpanded ? 'expanded' : ''}`}>
                                                        {item.children.map((child, subIndex) => {
                                                            const ChildIcon = child.icon;
                                                            return (
                                                                <li key={subIndex} className="submenu-item">
                                                                    {isMobile ? (
                                                                        <div
                                                                            className="nav-link submenu-link"
                                                                            onClick={() => handleMobileNavigation(child.path)}
                                                                            title={child.description}
                                                                        >
                                                                            <div className="nav-link-content">
                                                                                <div className="nav-icon submenu-icon">
                                                                                    <ChildIcon size={16} />
                                                                                </div>
                                                                                <span className="nav-label">{child.label}</span>
                                                                            </div>
                                                                        </div>
                                                                    ) : (
                                                                        <NavLink
                                                                            to={child.path}
                                                                            end
                                                                            className={({ isActive }) =>
                                                                                `nav-link submenu-link ${isActive ? 'active' : ''}`
                                                                            }
                                                                            title={child.description}
                                                                        >
                                                                            <div className="nav-link-content">
                                                                                <div className="nav-icon submenu-icon">
                                                                                    <ChildIcon size={16} />
                                                                                </div>
                                                                                <span className="nav-label">{child.label}</span>
                                                                            </div>
                                                                        </NavLink>
                                                                    )}
                                                                </li>
                                                            );
                                                        })}
                                                    </ul>
                                                </>
                                            )}
                                        </li>
                                    );
                                }

                                return (
                                    <li key={index} className="nav-item">
                                        {isMobile ? (
                                            <div
                                                className="nav-link"
                                                onClick={() => handleMobileNavigation(item.path, item.externalUrl)}
                                                title={item.description}
                                            >
                                                <div className="nav-link-content">
                                                    <div className="nav-icon">
                                                        <IconComponent size={17} />
                                                    </div>
                                                    {((!isCollapsed && !isTablet) || isMobile) && (
                                                        <span className="nav-label">{item.label}</span>
                                                    )}
                                                    {item.isNew && ((!isCollapsed && !isTablet) || isMobile) && (
                                                        <span className="new-badge">NEW</span>
                                                    )}
                                                </div>
                                            </div>
                                        ) : item.externalUrl ? (
                                            <a
                                                href={item.externalUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="nav-link"
                                                title={`${item.description} · se abre en una pestaña nueva`}
                                            >
                                                <div className="nav-link-content">
                                                    <div className="nav-icon">
                                                        <IconComponent size={17} />
                                                    </div>
                                                    {(!isCollapsed && !isTablet) && (
                                                        <span className="nav-label">{item.label}</span>
                                                    )}
                                                    {/* Con el badge NEW no cabe además la flecha de enlace
                                                        externo sin comerse el final de la etiqueta: cuando
                                                        hay badge, el aviso queda en el title del enlace. */}
                                                    {item.isNew && (!isCollapsed && !isTablet) && (
                                                        <span className="new-badge">NEW</span>
                                                    )}
                                                    {!item.isNew && (!isCollapsed && !isTablet) && (
                                                        <span className="nav-arrow nav-external">
                                                            <FaExternalLinkAlt size={12} />
                                                        </span>
                                                    )}
                                                </div>
                                            </a>
                                        ) : (
                                            <NavLink
                                                to={item.path}
                                                className={({ isActive }) =>
                                                    `nav-link ${isActive ? 'active' : ''}`
                                                }
                                                title={item.description}
                                            >
                                                <div className="nav-link-content">
                                                    <div className="nav-icon">
                                                        <IconComponent size={17} />
                                                    </div>
                                                    {((!isCollapsed && !isTablet) || isMobile) && (
                                                        <span className="nav-label">{item.label}</span>
                                                    )}
                                                    {item.isNew && ((!isCollapsed && !isTablet) || isMobile) && (
                                                        <span className="new-badge">NEW</span>
                                                    )}
                                                </div>
                                            </NavLink>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                </nav>

                {/* Footer del sidebar */}
                <div className="sidebar-footer">
                    {user && onLogout && (
                        <button
                            className="logout-btn"
                            onClick={handleLogout}
                            title="Cerrar sesión"
                        >
                            <FaSignOutAlt size={16} />
                            {((!isCollapsed && !isTablet) || isMobile) && <span>Cerrar Sesión</span>}
                        </button>
                    )}
                    <div className="footer-info">
                        <small className="copyright">
                            {((isCollapsed || isTablet) && !isMobile) ? "© 2026" : "© 2026 Nuvia Consulting"}
                        </small>
                    </div>
                </div>
            </div>

            {/* Menú flotante para desktop/tablet colapsado */}
            {!isMobile && floatingSubmenu.isOpen && floatingSubmenu.menuIndex !== null && (
                <div
                    className="floating-submenu"
                    style={{
                        position: 'fixed',
                        top: floatingSubmenu.position.top,
                        left: floatingSubmenu.position.left,
                        zIndex: 10001
                    }}
                >
                    <div className="floating-submenu-header">
                        <div className="floating-submenu-icon">
                            {(() => {
                                const IconComponent = menuItems[floatingSubmenu.menuIndex]?.icon;
                                return IconComponent ? <IconComponent size={16} /> : null;
                            })()}
                        </div>
                        <span className="floating-submenu-title">
                            {menuItems[floatingSubmenu.menuIndex]?.label}
                        </span>
                        <button
                            className="floating-submenu-close"
                            onClick={closeFloatingSubmenu}
                        >
                            ×
                        </button>
                    </div>
                    <div className="floating-submenu-content">
                        {menuItems[floatingSubmenu.menuIndex]?.children?.map((child, subIndex) => {
                            const ChildIcon = child.icon;
                            return (
                                <NavLink
                                    key={subIndex}
                                    to={child.path}
                                    className={({ isActive }) =>
                                        `floating-submenu-item ${isActive ? 'active' : ''}`
                                    }
                                    onClick={closeFloatingSubmenu}
                                >
                                    <div className="floating-submenu-item-content">
                                        <div className="floating-submenu-item-icon">
                                            <ChildIcon size={16} />
                                        </div>
                                        <div className="floating-submenu-text">
                                            <div className="floating-submenu-label">{child.label}</div>
                                            <div className="floating-submenu-description">{child.description}</div>
                                        </div>
                                    </div>
                                </NavLink>
                            );
                        })}
                    </div>
                </div>
            )}

            <style jsx global>{`
                @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&display=swap');

                :root {
                    --sidebar-width: 280px;
                    --sidebar-collapsed-width: 72px;
                    --sb-bg: #0f1520;
                    --sb-surface: #1c2534;
                    --sb-text: #cbd5e1;
                    --sb-text-strong: #f1f5f9;
                    --sb-text-muted: #94a3b8;
                    --sb-icon: #7c8ba1;
                    --sb-chevron: #475569;
                    --sb-border: rgba(255, 255, 255, 0.07);
                    --sb-border-strong: rgba(255, 255, 255, 0.09);
                    --sb-hover: rgba(255, 255, 255, 0.05);
                    --sb-accent: #0f766e;
                    --sb-accent-hover: #0b5c55;
                    --sb-new: #5eead4;
                    --transition: all 0.22s cubic-bezier(0.4, 0, 0.2, 1);
                    --shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.35), 0 4px 6px -2px rgba(0, 0, 0, 0.25);
                    --shadow-xl: 0 20px 30px -8px rgba(0, 0, 0, 0.5);
                }

                * {
                    box-sizing: border-box;
                }

                body {
                    margin: 0;
                    padding: 0;
                    overflow-x: hidden;
                }

                .app-container {
                    display: flex;
                    min-height: 100vh;
                    position: relative;
                }

                .main-content {
                    flex: 1;
                    margin-left: var(--sidebar-width);
                    transition: margin-left 0.22s cubic-bezier(0.4, 0, 0.2, 1), width 0.22s cubic-bezier(0.4, 0, 0.2, 1);
                    min-height: 100vh;
                    background: #f8fafc;
                    width: calc(100vw - var(--sidebar-width));
                    overflow-x: hidden;
                    padding: 0 !important;
                }

                .main-content.sidebar-collapsed {
                    margin-left: var(--sidebar-collapsed-width);
                    width: calc(100vw - var(--sidebar-collapsed-width));
                }

                /* ===== Overlay + botón móvil ===== */
                .mobile-overlay {
                    position: fixed;
                    inset: 0;
                    width: 100vw;
                    height: 100vh;
                    background: rgba(8, 12, 18, 0.55);
                    z-index: 1099;
                    backdrop-filter: blur(3px);
                    animation: fadeIn 0.3s ease;
                }

                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }

                .mobile-toggle-btn {
                    position: fixed;
                    top: 15px;
                    left: 15px;
                    width: 46px;
                    height: 46px;
                    border-radius: 12px;
                    border: none;
                    background: linear-gradient(135deg, var(--sb-accent) 0%, var(--sb-accent-hover) 100%);
                    color: #fff;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: transform 0.22s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.22s cubic-bezier(0.4, 0, 0.2, 1);
                    box-shadow: 0 8px 20px rgba(15, 118, 110, 0.35);
                    backface-visibility: hidden;
                    z-index: 1200;
                }

                .mobile-toggle-btn:hover {
                    transform: scale(1.05);
                    box-shadow: 0 10px 26px rgba(15, 118, 110, 0.5);
                }

                .mobile-toggle-btn:active {
                    transform: scale(0.96);
                }

                /* ===== Shell del sidebar ===== */
                .sidebar {
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: var(--sidebar-width);
                    height: 100vh;
                    background: var(--sb-bg);
                    border-right: 1px solid var(--sb-border);
                    display: flex;
                    flex-direction: column;
                    transition: width 0.22s cubic-bezier(0.4, 0, 0.2, 1), transform 0.22s cubic-bezier(0.4, 0, 0.2, 1);
                    z-index: 1051;
                    font-family: 'IBM Plex Sans', system-ui, -apple-system, sans-serif;
                    color: var(--sb-text);
                }

                .sidebar.collapsed {
                    width: var(--sidebar-collapsed-width);
                }

                .sidebar.tablet {
                    width: var(--sidebar-collapsed-width);
                }

                .sidebar.mobile {
                    width: 280px;
                    transform: translateX(-100%);
                    z-index: 1100;
                    box-shadow: 14px 0 40px rgba(0, 0, 0, 0.4);
                }

                .sidebar.mobile:not(.collapsed) {
                    transform: translateX(0);
                }

                /* ===== Toggle (desktop) ===== */
                .toggle-container {
                    position: absolute;
                    top: 20px;
                    right: -14px;
                    z-index: 1001;
                    transform: translateZ(0);
                    backface-visibility: hidden;
                }

                .toggle-btn {
                    width: 28px;
                    height: 28px;
                    border-radius: 50%;
                    border: 1px solid var(--sb-border-strong);
                    background: var(--sb-surface);
                    color: var(--sb-text);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: background-color 0.22s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.22s cubic-bezier(0.4, 0, 0.2, 1), color 0.22s cubic-bezier(0.4, 0, 0.2, 1);
                    box-shadow: var(--shadow-lg);
                    transform: translateZ(0);
                    backface-visibility: hidden;
                }

                .toggle-btn:hover {
                    background: var(--sb-accent);
                    border-color: var(--sb-accent);
                    color: #fff;
                }

                /* ===== Header ===== */
                .sidebar-header {
                    padding: 20px 18px 18px;
                    border-bottom: 1px solid var(--sb-border);
                    flex-shrink: 0;
                }

                .sidebar.mobile .sidebar-header {
                    padding: 18px 16px;
                }

                .sidebar.tablet .sidebar-header {
                    padding: 18px 12px;
                }

                .sidebar-brand {
                    display: flex;
                    align-items: center;
                    gap: 11px;
                }

                .collapsed .sidebar-brand,
                .tablet .sidebar-brand {
                    justify-content: center;
                }

                .sidebar.mobile .sidebar-brand {
                    justify-content: flex-start;
                }

                .logo-container {
                    flex-shrink: 0;
                }

                .company-logo {
                    width: 40px;
                    height: 40px;
                    border-radius: 10px;
                    object-fit: cover;
                    background: var(--sb-surface);
                    border: 1px solid var(--sb-border-strong);
                    transition: border-color 0.22s cubic-bezier(0.4, 0, 0.2, 1);
                }

                .company-logo:hover {
                    border-color: var(--sb-accent);
                }

                .brand-text {
                    min-width: 0;
                    transition: opacity 0.22s cubic-bezier(0.4, 0, 0.2, 1), width 0.22s cubic-bezier(0.4, 0, 0.2, 1);
                }

                .collapsed .brand-text,
                .tablet .brand-text {
                    opacity: 0;
                    width: 0;
                    overflow: hidden;
                }

                .sidebar.mobile .brand-text {
                    opacity: 1;
                    width: auto;
                    overflow: visible;
                }

                .brand-name {
                    font-size: 14px;
                    font-weight: 600;
                    color: var(--sb-text-strong);
                    margin: 0;
                    line-height: 1.2;
                    letter-spacing: -0.01em;
                    white-space: nowrap;
                }

                .brand-subtitle {
                    font-size: 11px;
                    color: var(--sb-text-muted);
                    font-weight: 400;
                    white-space: nowrap;
                }

                /* ===== Usuario ===== */
                .user-section {
                    padding: 14px 18px;
                    border-bottom: 1px solid var(--sb-border);
                    flex-shrink: 0;
                }

                .sidebar.mobile .user-section {
                    padding: 14px 16px;
                }

                .sidebar.tablet .user-section {
                    padding: 14px 12px;
                }

                .user-info {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                }

                .collapsed .user-info,
                .tablet .user-info {
                    justify-content: center;
                }

                .sidebar.mobile .user-info {
                    justify-content: flex-start;
                }

                .user-avatar-container {
                    position: relative;
                    flex-shrink: 0;
                }

                .user-avatar {
                    width: 30px;
                    height: 30px;
                    border-radius: 50%;
                    object-fit: cover;
                    background: #1e293b;
                    border: 1px solid var(--sb-border-strong);
                }

                .user-avatar-placeholder {
                    width: 30px;
                    height: 30px;
                    border-radius: 50%;
                    background: #1e293b;
                    color: var(--sb-text);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 12px;
                    font-weight: 600;
                    border: 1px solid var(--sb-border-strong);
                    transition: var(--transition);
                }

                .user-avatar-placeholder:hover {
                    border-color: var(--sb-accent);
                }

                .user-status {
                    position: absolute;
                    bottom: -1px;
                    right: -1px;
                    width: 9px;
                    height: 9px;
                    background: #10b981;
                    border: 2px solid var(--sb-bg);
                    border-radius: 50%;
                }

                .user-details {
                    min-width: 0;
                    flex: 1;
                }

                .collapsed .user-details,
                .tablet .user-details {
                    display: none;
                }

                .user-name {
                    font-size: 12.5px;
                    font-weight: 600;
                    color: #e2e8f0;
                    line-height: 1.3;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }

                .user-email {
                    font-size: 11px;
                    color: var(--sb-text-muted);
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }

                .sidebar .user-plan-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 5px;
                    max-width: 100%;
                    margin-top: 7px;
                    padding: 4px 8px;
                    border: 1px solid #28655d;
                    border-radius: 6px;
                    background: #123c38;
                    color: #99f6e4;
                    font-family: inherit;
                    font-size: 10.5px;
                    font-weight: 600;
                    line-height: 1.4;
                    vertical-align: top;
                }

                .sidebar .user-plan-badge svg {
                    flex-shrink: 0;
                }

                .sidebar .user-plan-badge span {
                    min-width: 0;
                    overflow-wrap: anywhere;
                }

                /* ===== Navegación ===== */
                .sidebar-nav {
                    flex: 1;
                    padding: 14px 12px;
                    overflow-y: auto;
                    overflow-x: hidden;
                    scroll-behavior: smooth;
                }

                .sidebar.mobile .sidebar-nav {
                    padding: 14px 12px;
                }

                .sidebar.tablet .sidebar-nav {
                    padding: 14px 8px;
                }

                .sidebar-nav::-webkit-scrollbar {
                    width: 4px;
                }

                .sidebar-nav::-webkit-scrollbar-track {
                    background: transparent;
                }

                .sidebar-nav::-webkit-scrollbar-thumb {
                    background: rgba(148, 163, 184, 0.25);
                    border-radius: 2px;
                }

                .nav-section-title {
                    font-size: 9.5px;
                    font-weight: 600;
                    color: var(--sb-text-muted);
                    text-transform: uppercase;
                    letter-spacing: 0.09em;
                    padding: 0 8px 9px;
                }

                .collapsed .nav-section-title,
                .tablet .nav-section-title {
                    opacity: 0;
                    height: 0;
                    margin: 0;
                    padding: 0;
                    overflow: hidden;
                }

                .nav-list {
                    list-style: none;
                    padding: 0;
                    margin: 0;
                    display: flex;
                    flex-direction: column;
                    gap: 1px;
                }

                .nav-item {
                    animation: slideInLeft 0.3s ease forwards;
                }

                .nav-link {
                    display: block;
                    padding: 9px 11px;
                    border-radius: 8px;
                    color: var(--sb-text);
                    text-decoration: none;
                    cursor: pointer;
                    transition: var(--transition);
                    position: relative;
                }

                .nav-link:visited {
                    color: var(--sb-text);
                }

                .collapsed .nav-link,
                .tablet .nav-link {
                    padding: 10px;
                    display: flex;
                    justify-content: center;
                }

                .sidebar.mobile .nav-link {
                    padding: 11px;
                    font-size: 0.9rem;
                }

                .nav-link:hover {
                    background: var(--sb-hover);
                    color: var(--sb-text-strong);
                }

                .nav-link.active {
                    background: var(--sb-accent);
                    color: #fff;
                    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
                }

                .nav-link.active:hover {
                    background: var(--sb-accent);
                    color: #fff;
                }

                .nav-link-content {
                    display: flex;
                    align-items: center;
                    gap: 11px;
                }

                .collapsed .nav-link-content,
                .tablet .nav-link-content {
                    justify-content: center;
                    gap: 0;
                }

                .sidebar.mobile .nav-link-content {
                    justify-content: flex-start;
                    gap: 11px;
                }

                .nav-icon {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 22px;
                    min-width: 22px;
                    height: 22px;
                    color: var(--sb-icon);
                    flex-shrink: 0;
                    transition: var(--transition);
                    position: relative;
                }

                .nav-icon svg {
                    width: 20px;
                    height: 20px;
                }

                .nav-link:hover .nav-icon {
                    color: var(--sb-text-strong);
                }

                .nav-link.active .nav-icon {
                    color: #fff;
                }

                .nav-label {
                    font-size: 13.5px;
                    font-weight: 500;
                    white-space: nowrap;
                    /* Ocupa el hueco libre y se recorta con puntos suspensivos:
                       así el texto nunca se mete debajo del badge NEW ni de la flecha. */
                    flex: 1 1 auto;
                    min-width: 0;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    transition: var(--transition);
                }

                .nav-link.active .nav-label {
                    font-weight: 600;
                }

                .collapsed .nav-label,
                .tablet .nav-label {
                    display: none;
                }

                .sidebar.mobile .nav-label {
                    display: inline;
                    font-size: 0.9rem;
                }

                .new-badge {
                    display: inline-flex;
                    align-items: center;
                    margin-left: auto;
                    flex-shrink: 0;
                    font-size: 8.5px;
                    font-weight: 700;
                    letter-spacing: 0.03em;
                    text-transform: uppercase;
                    color: var(--sb-new);
                    background: transparent;
                    border: 1px solid rgba(94, 234, 212, 0.3);
                    padding: 2px 5px;
                    border-radius: 4px;
                    line-height: 1.5;
                }

                .nav-link.active .new-badge {
                    color: #fff;
                    border-color: rgba(255, 255, 255, 0.4);
                }

                .nav-arrow {
                    margin-left: auto;
                    display: flex;
                    align-items: center;
                    color: var(--sb-chevron);
                    flex-shrink: 0;
                    transition: var(--transition);
                }

                .nav-arrow svg {
                    width: 16px;
                    height: 16px;
                }

                /* El gap del contenedor ya separa el badge de la flecha:
                   un margen extra solo le robaba ancho a la etiqueta. */
                .new-badge + .nav-arrow {
                    margin-left: 0;
                }

                .nav-external svg {
                    width: 11px;
                    height: 11px;
                }

                .nav-link:hover .nav-arrow {
                    color: var(--sb-text-muted);
                }

                .nav-link.active .nav-arrow {
                    color: rgba(255, 255, 255, 0.85);
                }

                .parent-nav.expanded .nav-arrow {
                    color: var(--sb-text-strong);
                }

                /* Elementos colapsados con submenú */
                .parent-nav-collapsed {
                    cursor: pointer !important;
                    pointer-events: auto !important;
                    -webkit-tap-highlight-color: transparent;
                    touch-action: manipulation;
                }

                .parent-nav-collapsed .nav-link-content {
                    position: relative;
                }

                .submenu-indicator {
                    position: absolute;
                    top: -3px;
                    right: -3px;
                    background: var(--sb-accent);
                    border-radius: 50%;
                    width: 15px;
                    height: 15px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #fff;
                    border: 2px solid var(--sb-bg);
                }

                .submenu-indicator svg {
                    width: 9px;
                    height: 9px;
                }

                /* Submenú en línea */
                .submenu {
                    list-style: none;
                    padding: 0;
                    margin: 4px 0 4px 30px;
                    max-height: 0;
                    overflow: hidden;
                    transition: var(--transition);
                }

                .sidebar.mobile .submenu {
                    margin-left: 34px;
                }

                .submenu.expanded {
                    max-height: 500px;
                    padding-bottom: 4px;
                }

                .submenu-item {
                    margin-bottom: 1px;
                }

                .submenu-link {
                    padding: 8px 11px;
                    border-radius: 6px;
                }

                .submenu-link .nav-label {
                    font-size: 12.5px;
                    font-weight: 500;
                }

                .submenu-icon {
                    width: 18px;
                    min-width: 18px;
                    height: 18px;
                    color: var(--sb-icon);
                }

                .submenu-icon svg {
                    width: 15px;
                    height: 15px;
                }

                .submenu-link.active {
                    background: var(--sb-accent);
                    color: #fff;
                }

                .submenu-link.active .submenu-icon {
                    color: #fff;
                }

                /* ===== Menú flotante (colapsado) ===== */
                .floating-submenu {
                    background: #131b28;
                    border: 1px solid var(--sb-border);
                    border-radius: 12px;
                    box-shadow: var(--shadow-xl);
                    min-width: 260px;
                    max-width: 300px;
                    overflow: hidden;
                    animation: floatingMenuAppear 0.18s ease-out;
                    font-family: 'IBM Plex Sans', system-ui, sans-serif;
                }

                @keyframes floatingMenuAppear {
                    from {
                        opacity: 0;
                        transform: scale(0.97) translateY(-6px);
                    }
                    to {
                        opacity: 1;
                        transform: scale(1) translateY(0);
                    }
                }

                .floating-submenu-header {
                    background: rgba(255, 255, 255, 0.03);
                    padding: 14px 16px;
                    border-bottom: 1px solid var(--sb-border);
                    display: flex;
                    align-items: center;
                    gap: 11px;
                    position: relative;
                }

                .floating-submenu-icon {
                    color: var(--sb-new);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .floating-submenu-title {
                    font-weight: 600;
                    color: var(--sb-text-strong);
                    font-size: 14px;
                    flex: 1;
                }

                .floating-submenu-close {
                    position: absolute;
                    top: 8px;
                    right: 8px;
                    background: none;
                    border: none;
                    color: var(--sb-text-muted);
                    font-size: 18px;
                    width: 26px;
                    height: 26px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: var(--transition);
                }

                .floating-submenu-close:hover {
                    background: rgba(248, 113, 113, 0.12);
                    color: #fca5a5;
                }

                .floating-submenu-content {
                    padding: 8px;
                }

                .floating-submenu-item {
                    display: block;
                    text-decoration: none;
                    color: inherit;
                    border-radius: 8px;
                    transition: var(--transition);
                }

                .floating-submenu-item:hover {
                    background: rgba(255, 255, 255, 0.05);
                }

                .floating-submenu-item.active {
                    background: var(--sb-accent);
                }

                .floating-submenu-item-content {
                    display: flex;
                    align-items: center;
                    gap: 11px;
                    padding: 10px 12px;
                }

                .floating-submenu-item-icon {
                    color: var(--sb-icon);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    min-width: 16px;
                    transition: var(--transition);
                }

                .floating-submenu-item.active .floating-submenu-item-icon {
                    color: #fff;
                }

                .floating-submenu-text {
                    flex: 1;
                    min-width: 0;
                }

                .floating-submenu-label {
                    font-weight: 600;
                    color: var(--sb-text-strong);
                    font-size: 13px;
                    line-height: 1.3;
                    margin-bottom: 2px;
                }

                .floating-submenu-item.active .floating-submenu-label {
                    color: #fff;
                }

                .floating-submenu-description {
                    font-size: 11px;
                    color: var(--sb-text-muted);
                    line-height: 1.4;
                    white-space: normal;
                    word-wrap: break-word;
                }

                .floating-submenu-item.active .floating-submenu-description {
                    color: rgba(255, 255, 255, 0.8);
                }

                /* ===== Footer ===== */
                .sidebar-footer {
                    padding: 14px 16px 16px;
                    border-top: 1px solid var(--sb-border);
                    flex-shrink: 0;
                }

                .sidebar.mobile .sidebar-footer {
                    padding: 14px 16px;
                }

                .sidebar.tablet .sidebar-footer {
                    padding: 14px 8px;
                }

                .logout-btn {
                    width: 100%;
                    padding: 10px;
                    background: transparent;
                    border: 1px solid var(--sb-border-strong);
                    border-radius: 8px;
                    color: var(--sb-text-muted);
                    font-size: 12.5px;
                    font-weight: 500;
                    font-family: inherit;
                    cursor: pointer;
                    transition: var(--transition);
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    justify-content: center;
                    margin-bottom: 11px;
                    position: relative;
                }

                .logout-btn:hover {
                    border-color: rgba(248, 113, 113, 0.4);
                    color: #fca5a5;
                    background: rgba(248, 113, 113, 0.06);
                }

                .logout-btn:active {
                    transform: translateY(1px);
                }

                .collapsed .logout-btn,
                .tablet .logout-btn {
                    padding: 10px;
                    justify-content: center;
                }

                .collapsed .logout-btn span,
                .tablet .logout-btn span {
                    display: none;
                }

                .sidebar.mobile .logout-btn {
                    padding: 12px;
                    justify-content: center;
                }

                .sidebar.mobile .logout-btn span {
                    display: inline;
                }

                .footer-info {
                    text-align: center;
                }

                .copyright {
                    color: var(--sb-text-muted);
                    font-size: 10.5px;
                }

                /* ===== Tooltips en modo colapsado ===== */
                .collapsed .nav-link:not(.parent-nav-collapsed)::after,
                .tablet .nav-link:not(.parent-nav-collapsed)::after {
                    content: attr(title);
                    position: absolute;
                    left: calc(100% + 14px);
                    top: 50%;
                    transform: translateY(-50%);
                    background: #1f2937;
                    color: #fff;
                    padding: 7px 11px;
                    border-radius: 6px;
                    font-size: 12px;
                    white-space: nowrap;
                    opacity: 0;
                    pointer-events: none;
                    transition: var(--transition);
                    z-index: 10000;
                    box-shadow: var(--shadow-lg);
                }

                .collapsed .nav-link:not(.parent-nav-collapsed):hover::after,
                .tablet .nav-link:not(.parent-nav-collapsed):hover::after {
                    opacity: 1;
                }

                .collapsed .logout-btn::after,
                .tablet .logout-btn::after {
                    content: 'Cerrar Sesión';
                    position: absolute;
                    left: calc(100% + 14px);
                    top: 50%;
                    transform: translateY(-50%);
                    background: #1f2937;
                    color: #fff;
                    padding: 7px 11px;
                    border-radius: 6px;
                    font-size: 12px;
                    white-space: nowrap;
                    opacity: 0;
                    pointer-events: none;
                    transition: var(--transition);
                    z-index: 10000;
                    box-shadow: var(--shadow-lg);
                }

                .collapsed .logout-btn:hover::after,
                .tablet .logout-btn:hover::after {
                    opacity: 1;
                }

                /* ===== Animaciones ===== */
                @keyframes slideInLeft {
                    from {
                        opacity: 0;
                        transform: translateX(-14px);
                    }
                    to {
                        opacity: 1;
                        transform: translateX(0);
                    }
                }

                .nav-item:nth-child(1) { animation-delay: 0.02s; }
                .nav-item:nth-child(2) { animation-delay: 0.05s; }
                .nav-item:nth-child(3) { animation-delay: 0.08s; }
                .nav-item:nth-child(4) { animation-delay: 0.11s; }
                .nav-item:nth-child(5) { animation-delay: 0.14s; }
                .nav-item:nth-child(6) { animation-delay: 0.17s; }
                .nav-item:nth-child(7) { animation-delay: 0.20s; }
                .nav-item:nth-child(8) { animation-delay: 0.23s; }
                .nav-item:nth-child(9) { animation-delay: 0.26s; }
                .nav-item:nth-child(10) { animation-delay: 0.29s; }
                .nav-item:nth-child(11) { animation-delay: 0.32s; }
                .nav-item:nth-child(12) { animation-delay: 0.35s; }
                .nav-item:nth-child(13) { animation-delay: 0.38s; }
                .nav-item:nth-child(14) { animation-delay: 0.41s; }

                .nav-link:focus,
                .toggle-btn:focus,
                .logout-btn:focus,
                .mobile-toggle-btn:focus {
                    outline: 2px solid var(--sb-accent);
                    outline-offset: 2px;
                }

                /* ===== Responsive ===== */
                @media (max-width: 768px) {
                    .main-content {
                        margin-left: 0 !important;
                        width: 100vw !important;
                        padding-top: 70px;
                    }

                    .main-content.sidebar-collapsed {
                        margin-left: 0 !important;
                        width: 100vw !important;
                    }

                    .nav-link::after,
                    .logout-btn::after {
                        display: none !important;
                    }

                    .sidebar.mobile {
                        transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    }

                    .mobile-overlay {
                        transition: opacity 0.3s ease;
                    }
                }

                @media (min-width: 769px) and (max-width: 1024px) {
                    .main-content {
                        margin-left: var(--sidebar-collapsed-width);
                        width: calc(100vw - var(--sidebar-collapsed-width));
                    }

                    .main-content.sidebar-collapsed {
                        margin-left: var(--sidebar-collapsed-width);
                        width: calc(100vw - var(--sidebar-collapsed-width));
                    }
                }

                @media (min-width: 1025px) {
                    .tablet {
                        display: none;
                    }
                }

                .main-content .container-fluid {
                    max-width: 100%;
                    padding-left: 1rem;
                    padding-right: 1rem;
                }

                .main-content .table-responsive {
                    max-width: 100%;
                    overflow-x: auto;
                }

                @media (min-width: 1440px) {
                    .main-content {
                        padding-left: 2rem;
                        padding-right: 2rem;
                    }
                }
            `}
            </style>
        </>
    );
};

export default Sidebar;
